import React, { useMemo, useState, type FC } from 'react'
import { useIntl } from 'react-intl'
import { useAtom } from 'jotai'
import styled from '@emotion/styled'

import {
  FormElement,
  Select,
  GridCell,
  InputNumber,
  Text,
  DatePicker,
} from 'mailup-web-components'

import { type ConditionProps } from '../../types'
import { filterFormImmerAtom } from '../../state/state'
import {
  type UiActivityConditionType,
  type UiActivityDateRangeValue,
  type UiActivityDateType,
  type UiActivitySubsSpec,
} from '../../filterConditionTypes'
import { activityDateTypeOpts } from '../../utils/conditionOptions'
import {
  getActivityDateNumberInputFollowText,
  getDefaultActivityDateValue,
} from '../../utils/conditionUtils'
import { datePickerDateFormat } from '../../../../config/config'
import {
  activityDateErrorMessageId,
  getDataRentionDays,
  isUiActivityDateRangeValue,
  validateActivityDate,
} from '../../utils/activitySpecValidation'
import ActivityRetentionText from './ActivityRetentionText'

const StyledText = styled(Text)`
  padding-top: 8px;
`

const ActivityDateDetailsCondition: FC<ConditionProps> = ({
  blockIndex,
  condIndex,
}) => {
  const intl = useIntl()
  const [filter, setFilter] = useAtom(filterFormImmerAtom)
  const condition = filter.Blocks[blockIndex].Conditions[condIndex]
  const spec = condition.Spec as Partial<UiActivitySubsSpec>
  const [isValueDirty, setIsValueDirty] = useState(false)
  const feedback =
    isValueDirty && !validateActivityDate(condition)
      ? activityDateErrorMessageId(condition, intl)
      : undefined

  const [maxFromDate, setMaxFromDate] = useState(
    spec.DateValue !== undefined &&
      isUiActivityDateRangeValue(spec.DateValue) &&
      spec.DateValue.To !== null
      ? spec.DateValue.To
      : null,
  )
  const [minToDate, setMinToDate] = useState(
    spec.DateValue !== undefined &&
      isUiActivityDateRangeValue(spec.DateValue) &&
      spec.DateValue.From !== null
      ? spec.DateValue.From
      : null,
  )

  const dateTypeOptions = useMemo(() => activityDateTypeOpts(intl), [intl])

  const minFetchDate = useMemo(() => {
    const cond = condition.ConditionType as UiActivityConditionType
    const retentionDays = getDataRentionDays(cond)
    const retentionOffsetInMs = 24 * 60 * 60 * 1000 * retentionDays
    return new Date(Date.now() - retentionOffsetInMs)
  }, [condition.ConditionType])

  const handleDateTypeChange = (t: UiActivityDateType): void => {
    setIsValueDirty(false)
    const defaultDateValue = getDefaultActivityDateValue(t)
    setFilter((draft) => {
      const spec = draft.Blocks[blockIndex].Conditions[condIndex]
        .Spec as Partial<UiActivitySubsSpec>
      spec.DateType = t
      spec.DateValue = defaultDateValue
    })
  }

  const handleIntValueChange = (value: number): void => {
    setIsValueDirty(true)
    setFilter((draft) => {
      const spec = draft.Blocks[blockIndex].Conditions[condIndex]
        .Spec as Partial<UiActivitySubsSpec>
      spec.DateValue = value
    })
  }

  const handleFromRangeChange = (value: Date): void => {
    setMinToDate(value)
    setIsValueDirty(true)
    setFilter((draft) => {
      const spec = draft.Blocks[blockIndex].Conditions[condIndex]
        .Spec as Partial<UiActivitySubsSpec>
      ;(spec.DateValue as UiActivityDateRangeValue).From = value
    })
  }

  const handleToRangeChange = (value: Date): void => {
    setMaxFromDate(value)
    setIsValueDirty(true)
    setFilter((draft) => {
      const spec = draft.Blocks[blockIndex].Conditions[condIndex]
        .Spec as Partial<UiActivitySubsSpec>
      ;(spec.DateValue as UiActivityDateRangeValue).To = value
    })
  }

  const shouldRenderDateValue = useMemo(
    () =>
      spec.DateType !== undefined &&
      getDefaultActivityDateValue(spec.DateType) !== null,
    [spec.DateType],
  )
  const shouldRenderDateValueAsNumber = useMemo(
    () =>
      shouldRenderDateValue &&
      typeof getDefaultActivityDateValue(spec.DateType) === 'number',
    [spec.DateType, shouldRenderDateValue],
  )
  const shouldRenderDateValueAsCalendars = useMemo(() => {
    const defaultValue = getDefaultActivityDateValue(spec.DateType)
    return (
      shouldRenderDateValue &&
      defaultValue !== undefined &&
      isUiActivityDateRangeValue(defaultValue)
    )
  }, [spec.DateType, shouldRenderDateValue])
  const numberInputFollowText = useMemo(
    () =>
      spec.DateType !== undefined
        ? getActivityDateNumberInputFollowText(spec.DateType, intl)
        : '',
    [spec.DateType, intl],
  )

  const fromRangeProps = {
    value:
      spec.DateValue !== undefined && isUiActivityDateRangeValue(spec.DateValue)
        ? spec.DateValue.From
        : null,
    callback: handleFromRangeChange,
    locale: intl.locale,
    dateFormat: datePickerDateFormat,
    showTimeSelect: true,
    isClearable: false,
    invalid: feedback !== undefined,
    placeholder: intl.formatMessage({ id: 'Load.MailupCommon.FromDate' }),
    maxDate: maxFromDate ?? null,
    minDate: minFetchDate,
  }

  const toRangeProps = {
    value:
      spec.DateValue !== undefined && isUiActivityDateRangeValue(spec.DateValue)
        ? spec.DateValue.To
        : null,
    callback: handleToRangeChange,
    locale: intl.locale,
    dateFormat: datePickerDateFormat,
    showTimeSelect: true,
    isClearable: false,
    invalid: feedback !== undefined,
    placeholder: intl.formatMessage({ id: 'Load.MailupCommon.ToDate' }),
    minDate: minToDate ?? minFetchDate,
  }

  return (
    <>
      <GridCell span={3}>
        <FormElement>
          <Select
            options={dateTypeOptions}
            value={spec.DateType}
            onChange={handleDateTypeChange}
            data-mu-test='input-select-DateType'
          />
        </FormElement>
      </GridCell>
      <GridCell span={3}>
        {shouldRenderDateValueAsNumber && (
          <FormElement
            data-mu-test='filterForm-UiDateSpec-values_element'
            feedback={feedback}
          >
            <InputNumber
              data-mu-test='filterForm-UiDateSpec-values_input'
              name='UiDateSpec-values'
              value={spec.DateValue}
              onChange={handleIntValueChange}
              maxLength={8}
              precision={0}
              step={1}
              pattern={'\\d+'}
              invalid={feedback !== undefined}
            />
          </FormElement>
        )}
        {shouldRenderDateValueAsCalendars && (
          <FormElement
            feedback={
              feedback !== undefined
                ? intl.formatMessage({
                    id: 'Load.Filters.ActivityDatesErrorRequired',
                  })
                : undefined
            }
          >
            <DatePicker {...fromRangeProps} />
          </FormElement>
        )}
      </GridCell>
      <GridCell span={3}>
        {shouldRenderDateValueAsNumber && (
          <StyledText>{numberInputFollowText}</StyledText>
        )}
        {shouldRenderDateValueAsCalendars && (
          <FormElement feedback={feedback !== undefined ? ' ' : undefined}>
            <DatePicker {...toRangeProps} />
          </FormElement>
        )}
      </GridCell>
      <ActivityRetentionText />
    </>
  )
}

export default ActivityDateDetailsCondition
